import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Chip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { CAT_YELLOW, CAT_BLACK, CAT_TEXT, CAT_BORDER, CAT_GRAY } from '../utils/styles';
import { BulletItem } from '../utils/BulletItem';
import { DividerWithLabel } from '../utils/DividerWithLabel';
import { PocBox } from '../utils/PocBox';
const PSEUDO_EXTRA_STEPS: Step[] = [
  {
    label: 'Gather Required Accounts and Clients',
    link: null,
    screenshot: null,
    fields: [
      {
        name: 'CWS Account Naming Format',
        note: 'Format: -<capability_if_any>- (max 20 chars).\nCAT → ent-mc-cat1 | DLR → ent-mc-dlr1 | CUST → ent-mc-cust1\nWith capability: ent-mc-denied-dlr1',
      },
      {
        name: 'Client Naming Format',
        note: 'Format: pfm_svcs_<svc>_<env>_cc_client\nCAT PROD → pfm_svcs_ent_mc_fm3_cat1_prod_cc_client\nDLR QA → pfm_svcs_ent_mc_fm3_dlr1_np_cc_client',
      },
      {
        name: 'Account Owners',
        note: 'CAT accounts: use cat.com email as owner.\nDLR and CUST accounts: use platformcoreservices01@gmail.com.',
      },
      {
        name: 'QA vs PROD',
        note: 'All QA accounts must have cataccounttype=TESTID to sync with QA PDS. Create separate clients and accounts per environment.',
      },
    ],
    note: 'List all required CWS accounts and client IDs following the naming conventions.',
  },
  {
    label: 'Request CWS Accounts',
    link: {
      href: 'https://cat.service-now.com/sp?id=sc_cat_item&sys_id=a973fc6d1b3d8c146ec90fe7cc4bcbf0',
      text: 'Open ServiceNow — I Need Something (CAT / DLR accounts)',
    },
    screenshot: null,
    fields: [
      { name: 'Nature of Request', note: 'Other' },
      { name: 'Do you know the assignment group?', note: 'Yes' },
      { name: 'Description', note: 'Create new CWS account for CAT/DLR QA & PROD' },
      { name: 'Assignment Group', note: 'IAM - Identity Access Management' },
      { name: 'Service', note: 'IdentityIQ (IIQ)' },
      { name: 'Additional Comments', note: 'Specify cataccounttype:TESTID for non-prod accounts.' },
      {
        name: 'CUST Accounts (different flow)',
        note: 'Create customer accounts manually on parts.cat.com. Then submit a request to IdentityIQ to add cataccounttype. Use the user identity endpoint to associate customer to a ucid. Vault accounts in CyberArk.',
      },
    ],
    note: 'One form per ticket when using bulk request. After submission the IAM team will send a Non-Standard CWS Account Questionnaire — fill it out accurately. Confirm account creation with owner, reset password, and update credentials in CyberArk.',
  },
];

interface CcFormField {
  name: string;
  note: string;
}

interface Step {
  label: string;
  link: { href: string; text: string } | null;
  screenshot: string | string[] | null;
  fields: CcFormField[];
  note: string;
}

const STEPS: Step[] = [
  {
    label: 'Create ServiceNow Ticket for PingFederate Team',
    link: {
      href: 'https://cat.service-now.com/now/nav/ui/classic/params/target/com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1%26sysparm_id%3Da973fc6d1b3d8c146ec90fe7cc4bcbf0%26sysparm_link_parent%3Db7947cb34f72c200ebb32ae6f110c74c%26sysparm_catalog%3D71f51e2c4f968600ebb32ae6f110c732%26sysparm_catalog_view%3Dcatalog_ITatWork%26sysparm_view%3Dcatalog_ITatWork',
      text: 'Open ServiceNow → I need something',
    },
    screenshot: ['/screenshots/snow-ticket.webp','/screenshots/snow-ticket2.webp'],
    fields: [
      { name: ' Requested For', note: 'Requestor Name' },
      { name: ' What is the nature of this request', note: 'Select “Other”' },
      { name: 'Description', note: 'Write an appropriate use case, why is this client id required' },
      { name: 'Service', note: 'Always enter “PingFederate”.' },
      { name: 'Assignment Group', note: 'Always enter “Federation Services Support”.' },
      {name: 'Additional Comments', note: 'Include the target environment (QA or PROD) and any relevant deadlines.' },
    ],
    note: 'Submit a separate ticket for each environment (QA and PROD). Once submitted, post the SNOW ticket link in your Azure DevOps story for traceability.',
  },
  {
    label: 'Fill Application Intake Form',
    link: {
      href: 'https://forms.office.com/Pages/ResponsePage.aspx?id=v3exzjsBq0mKnEq84yr8HlAHDg_3GpNArf3n0sG9TAVUOThOMTlIN0xRWVBZU05LS0lEQVdLQ0RWNiQlQCN0PWcu',
      text: 'Open Application Intake Form',
    },
    screenshot: ['/screenshots/form1.png','/screenshots/form2.png','/screenshots/form3.png','/screenshots/form4.png'],
    fields: [
      {
        name: 'MFA Enforcement for Entra ID Applications',
        note: 'MFA is enforced by default for user-interactive logins. Provide a rationale only if requesting an exemption — subject to IAM Architecture team approval.',
      },
      {
        name: 'ServiceNow Service Request Ticket Number',
        note: 'REQUIRED. Paste the SCTASK# from your SNOW ticket. Separate tickets required for Prod and Non-Prod.',
      },
      {
        name: 'Existing PingFederate Entity ID or Client ID',
        note: 'Migration only. Leave blank for new clients.',
      },
      {
        name: 'Owners / Contacts (Email Address)',
        note: 'Caterpillar employees only. Config details will be shared with these contacts.',
      },
      {
        name: 'Business Unit',
        note: 'Your business unit (e.g., CAT Digital).',
      },
      {
        name: 'Application / Client Name',
        note: 'Include environment in the name if Prod and Non-Prod need separate clients (e.g., MyApp-QA).',
      },
      {
        name: 'Application Environment',
        note: 'Select Prod or Non-Prod. Submit a separate form and SNOW ticket for each.',
      },
      {
        name: 'LeanIX (Formerly Troux) ID',
        note: 'Required for Entra ID / B2C registration. Find your ID in the LeanIX repository.',
      },
      {
        name: 'Contact Support Groups',
        note: 'CWS Group responsible for the application. Used for contact purposes only.',
      },
      {
        name: 'Updated LTA (Logical Technical Architecture)?',
        note: 'Select Yes if available. Without an LTA, only standard Entra ID / B2C config applies.',
      },
      {
        name: 'Data Classification',
        note: 'Determines additional conditional access policies. Typically "Green" for most applications.',
      },
      {
        name: 'Client / Application Registration Type',
        note: 'AC clients: select "Authorization Code (AC client) - OAuth 2.0". CC clients: select "Client Credentials". Note: .Net Developers always select OAuth 2.0.',
      },
    ],
    note: 'Submit the Intake Form separately for each environment (QA and PROD). This runs in parallel with the SNOW ticket.',
  },
  {
    label: 'Follow Up & Verify Token Generation',
    link: null,
    screenshot: null,
    fields: [],
    note: 'Monitor the SNOW ticket for completion. Once the client is created, verify by generating a token in next step.',
  },
];



export default function StepClientID({ classes }: { classes: any }) {
  const [clientType, setClientType] = useState<'AC' | 'CC' | 'Pseudo'>('AC');

  return (
    <>
      <Typography variant="body2" style={{ color: '#757575', marginBottom: 20 }}>
        Choose the client type that best fits your use case. Each type has different setup steps and access patterns.
      </Typography>

      {/* Type explainers */}
      <div className={classes.chipRow}>
        {(['AC', 'CC', 'Pseudo'] as const).map(t => (
          <Chip
            key={t}
            label={t === 'AC' ? 'AC — User-based' : t === 'CC' ? 'CC — Service-based' : 'Pseudo User'}
            clickable
            className={clientType === t ? classes.chipActive : classes.chipInactive}
            onClick={() => setClientType(t)}
          />
        ))}
      </div>

      <Accordion className={classes.subAccordion} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>
            {clientType === 'AC' && 'AC — When to use'}
            {clientType === 'CC' && 'CC — When to use'}
            {clientType === 'Pseudo' && 'Pseudo — When to use'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails}>
          {clientType === 'AC' && (
            <>
              <BulletItem text="User authenticates on behalf of themselves (human-in-the-loop)." classes={classes} />
              <BulletItem text="Ideal for dealer portals, customer apps, and PWAs." classes={classes} />
              <BulletItem text="Supports Web, Mobile, and PWA platforms." classes={classes} />
            </>
          )}
          {clientType === 'CC' && (
            <>
              <BulletItem text="Machine-to-machine communication with no user interaction." classes={classes} />
              <BulletItem text="Used by backend services, scheduled jobs, and microservices." classes={classes} />
              <BulletItem text="Requires a role and party number for service identity." classes={classes} />
            </>
          )}
          {clientType === 'Pseudo' && (
            <>
              <BulletItem text="Restricted to automated testing own API or SIT." classes={classes} />
              <BulletItem text="Used for testing scenarios requiring a specific catrecid." classes={classes} />
              <BulletItem text="Requires cataccounttype as TESTID for QA Clients." classes={classes} />
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <DividerWithLabel label="Setup Steps" classes={classes} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div
                style={{
                  minWidth: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: CAT_YELLOW,
                  color: CAT_BLACK,
                  fontWeight: 800,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <Typography style={{ fontSize: 14.5, color: CAT_TEXT, fontWeight: 600, lineHeight: 1.7, marginBottom: 6 }}>
                  {step.label}
                </Typography>
                {step.link && (
                  <a
                    href={step.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', fontSize: 13.5, color: '#1565C0', marginBottom: 8 }}
                  >
                    🔗 {step.link.text}
                  </a>
                )}
                <Typography style={{ fontSize: 13.5, color: '#757575', lineHeight: 1.6, marginBottom: step.fields.length > 0 || step.screenshot ? 6 : 0 }}>
                  {step.note}
                </Typography>
                {step.fields.length > 0 && (
                  <Accordion className={classes.subAccordion} style={{ marginTop: 6 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography style={{ fontSize: 13.5, fontWeight: 600, color: CAT_TEXT }}>Form Field Guide</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.subAccordionDetails}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                        {step.fields.map((field, fi) => (
                          <div key={fi} style={{ padding: '8px 12px', background: CAT_GRAY, borderLeft: `3px solid ${CAT_YELLOW}`, borderRadius: 4 }}>
                            <Typography style={{ fontWeight: 700, fontSize: 13.5, color: CAT_TEXT }}>{field.name}</Typography>
                            <Typography style={{ fontSize: 13, color: '#757575', marginTop: 2 }}>{field.note}</Typography>
                          </div>
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                )}
                {step.screenshot && (
                  <Accordion className={classes.subAccordion} style={{ marginTop: 4 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography style={{ fontSize: 13.5, fontWeight: 600, color: CAT_TEXT }}>Screenshot Guide</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.subAccordionDetails}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '70%',margin:'0 auto' }}>
                        {(Array.isArray(step.screenshot) ? step.screenshot : [step.screenshot]).map((src, si) => (
                          <img
                            key={si}
                            src={src}
                            alt={`${step.label} screenshot ${si + 1}`}
                            style={{ maxWidth: '100%', borderRadius: 6, border: `1px solid ${CAT_BORDER}` }}
                          />
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                )}
              </div>
            </div>
          ))}
        </div>
      {clientType === 'Pseudo' && (
        <>
          <DividerWithLabel label="Pseudo Client — Extra Steps" classes={classes} />
          <Typography variant="body2" style={{ color: '#757575', marginBottom: 12 }}>
            A pseudo client is ideal for validation scenarios where you need a service-based client <strong>with user context</strong>. No login redirection is required — it is often the best alternative to an AC client for testing.
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {PSEUDO_EXTRA_STEPS.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: CAT_YELLOW,
                    color: CAT_BLACK,
                    fontWeight: 800,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <Typography style={{ fontSize: 14.5, color: CAT_TEXT, fontWeight: 600, lineHeight: 1.7, marginBottom: 6 }}>
                    {step.label}
                  </Typography>
                  {step.link && (
                    <a
                      href={step.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', fontSize: 13.5, color: '#1565C0', marginBottom: 8 }}
                    >
                      🔗 {step.link.text}
                    </a>
                  )}
                  <Typography style={{ fontSize: 13.5, color: '#757575', lineHeight: 1.6, marginBottom: step.fields.length > 0 ? 6 : 0 }}>
                    {step.note}
                  </Typography>
                  {step.fields.length > 0 && (
                    <Accordion className={classes.subAccordion} style={{ marginTop: 6 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography style={{ fontSize: 13.5, fontWeight: 600, color: CAT_TEXT }}>Form Field Guide</Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.subAccordionDetails}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                          {step.fields.map((field, fi) => (
                            <div key={fi} style={{ padding: '8px 12px', background: CAT_GRAY, borderLeft: `3px solid ${CAT_YELLOW}`, borderRadius: 4 }}>
                              <Typography style={{ fontWeight: 700, fontSize: 13.5, color: CAT_TEXT }}>{field.name}</Typography>
                              <Typography style={{ fontSize: 13, color: '#757575', marginTop: 2 }}>{field.note}</Typography>
                            </div>
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <DividerWithLabel label="ETA" classes={classes} />
      <BulletItem text="Client ID generation: 5 business days after submission." classes={classes} />
      <BulletItem text="Credential email from IAM team arrives within 24 hours of generation." classes={classes} />

      <PocBox
        classes={classes}
        contact="Federation Services Support Team"
        email="Federation_Services_Support@cat.com"
        issues={['Credentials not received', 'Client ID rejected', 'Wrong platform type selected', 'Role assignment missing']}
      />
    </>
  )  
}